import { FC, useCallback, useEffect, useState } from "react";

import { isImpersonatedSession } from "@/auth/token";
import { useGlobalContext } from "@/hooks/use-global-context";
import { useManageMe } from "@/hooks/use-manage-me";

import { useDailyTodosViewer } from "@/modules/daily-todos/data/use-daily-todos-viewer";

import { useWhatsNewCards } from "./data/use-whats-new-cards";
import { getWhatsNewAudience } from "./lib/audience";
import { useWhatsNewOpenState } from "./lib/open-state";
import {
  readWhatsNewSeen,
  readWhatsNewShownDays,
  writeWhatsNewSeen,
  writeWhatsNewShownDays,
} from "./lib/storage";
import type { WhatsNewCardI } from "./lib/types";
import {
  getWhatsNewCardsToShow,
  isWhatsNewLive,
  markWhatsNewSeen,
  markWhatsNewShown,
  WhatsNewSeenI,
} from "./lib/visibility";
import { WhatsNewDialog } from "./whats-new-dialog";

/**
 * "What's new" at the start of the day's first visit (C-16 S0, GLE-8407;
 * Omar, 2026-10-25: "Anytime you're doing something new, there should be a
 * widget"). Mounted once in AppLayout. It waits for the user, their role and
 * every card source, decides once per page load which live cards to show,
 * and records the dismissal per user and day (C16-D311–D315).
 */

// Dismissals made on this page load, per user: the fallback when storage is
// blocked, so a remount never reopens what the viewer just closed.
const dismissedThisLoad = new Map<string, WhatsNewSeenI>();

/** Test hook: forget this page load's dismissals. */
export const resetWhatsNewPageLoad = () => {
  dismissedThisLoad.clear();
};

export const WhatsNewModule: FC = () => {
  const manage = useManageMe();
  const { glencocoUser } = useGlobalContext();
  const userId = glencocoUser?.id;

  // Read once: an impersonation starts and ends with a page load.
  const [isImpersonated] = useState(isImpersonatedSession);
  const audience = getWhatsNewAudience(manage, isImpersonated);
  // C-16 S6: who has To-Dos, decided here (this module reads the global
  // context anyway) so the card sources stay free of it.
  const dailyTodos = useDailyTodosViewer();
  const { cards, isSettled } = useWhatsNewCards(
    audience,
    manage.me?.features,
    dailyTodos.isKnown ? dailyTodos.isCaller : undefined
  );

  // Null until decided; decided once, so a refetch never pops it up again.
  const [shownCards, setShownCards] = useState<Array<WhatsNewCardI> | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (shownCards || !userId || !audience || !isSettled) {
      return;
    }

    const seen = {
      ...readWhatsNewSeen(userId),
      ...dismissedThisLoad.get(userId),
    };
    const now = new Date();
    const shownDays = readWhatsNewShownDays(userId);
    const toShow = getWhatsNewCardsToShow(cards, seen, now, shownDays);

    // Counted when shown: each card shows on at most its cap of ET days.
    if (toShow.length > 0) {
      writeWhatsNewShownDays(
        userId,
        markWhatsNewShown(
          shownDays,
          toShow.map((card) => card.id),
          cards
            .filter((card) => isWhatsNewLive(card, now))
            .map((card) => card.id),
          now
        )
      );
    }

    setShownCards(toShow);
    setIsOpen(toShow.length > 0);
  }, [audience, cards, isSettled, shownCards, userId]);

  // Tells the other login dialogs (C-16 S6's To-Dos gate) when to wait.
  const setOpenState = useWhatsNewOpenState((state) => state.set);

  useEffect(() => {
    setOpenState({ hasDecided: shownCards !== null, isOpen });
  }, [isOpen, setOpenState, shownCards]);

  const dismiss = useCallback(() => {
    setIsOpen(false);

    if (!userId || !shownCards?.length) {
      return;
    }

    const now = new Date();
    const shownIds = shownCards.map((card) => card.id);
    const liveIds = cards
      .filter((card) => isWhatsNewLive(card, now))
      .map((card) => card.id);
    const next = markWhatsNewSeen(
      readWhatsNewSeen(userId),
      shownIds,
      liveIds,
      now
    );

    dismissedThisLoad.set(
      userId,
      markWhatsNewSeen(dismissedThisLoad.get(userId) ?? {}, shownIds, [], now)
    );
    writeWhatsNewSeen(userId, next);
  }, [cards, shownCards, userId]);

  if (!shownCards?.length) {
    return null;
  }

  return (
    <WhatsNewDialog cards={shownCards} isOpen={isOpen} onDismiss={dismiss} />
  );
};
